/*
 * Раздел «Proxmox»: площадки Proxmox VE установки — живые данные их API (бэкенд viva-proxmox, токен только для
 * чтения) по вкладкам: площадки, виртуальные машины, сети, пулы, образы. ViVA-ресурсы (пул viva и viva/<имя>)
 * отмечены, остальное на площадке — внешнее, только чтение. Создание и удаление — шаблоны раздела (proxmox-*) →
 * коммит в viva/clusters → пайплайн с PROXMOX_RESOURCE; VM kubespray-кластеров — форма «Новый Kubernetes-кластер».
 */
import { Chip, Typography } from '@material-ui/core';
import {
  Content,
  ContentHeader,
  EmptyState,
  Header,
  Link,
  LinkButton,
  Page,
  Progress,
  ResponseErrorPanel,
  StatusError,
  StatusOK,
  StatusWarning,
  TabbedLayout,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { configApiRef, discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import {
  deleteResourceLink,
  fetchProxmoxSites,
  poolOwner,
  ProxmoxImage,
  ProxmoxNetwork,
  ProxmoxPool,
  ProxmoxSite,
  ProxmoxVm,
  vmOwner,
} from '../../lib/proxmox';
import { isProxmoxEnabled, routes } from '../../lib/viva';

type WithSite<T> = T & { site: string };
const withSite = <T,>(sites: ProxmoxSite[], pick: (s: ProxmoxSite) => T[]): WithSite<T>[] =>
  sites.flatMap(s => pick(s).map(x => ({ ...x, site: s.id })));

const gb = (bytes: number) => `${Math.round(bytes / 1024 ** 3)} ГБ`;
const percent = (used: number, total: number) => (total ? `${Math.round((used / total) * 100)}%` : '—');

const OWNER_LABEL = { resource: 'ресурс раздела', cluster: 'кластер ViVA', external: 'внешний' } as const;
const Owner = ({ owner }: { owner: keyof typeof OWNER_LABEL }) => (
  <Chip size="small" label={OWNER_LABEL[owner]} color={owner === 'external' ? 'default' : 'primary'} variant={owner === 'external' ? 'outlined' : 'default'} />
);

const Status = ({ status }: { status: string }) => {
  if (status === 'running' || status === 'online' || status === 'available') return <StatusOK>{status}</StatusOK>;
  if (status === 'stopped') return <StatusWarning>{status}</StatusWarning>;
  return <StatusError>{status || 'неизвестно'}</StatusError>;
};

const Create = ({ to, text }: { to: string; text: string }) => (
  <LinkButton to={to} color="primary" variant="contained">
    {text}
  </LinkButton>
);

const Delete = ({ site, kind, name }: { site: string; kind: 'vms' | 'pools' | 'networks'; name: string }) => (
  <Link to={deleteResourceLink(routes.deleteProxmoxResource, site, kind, name)}>Удалить</Link>
);

const SitesTab = ({ sites }: { sites: ProxmoxSite[] }) => {
  const columns: TableColumn<ProxmoxSite>[] = [
    { title: 'Площадка', field: 'id' },
    { title: 'API', field: 'endpoint' },
    { title: 'Версия PVE', render: s => s.version ?? '—' },
    { title: 'Состояние', render: s => (s.error ? <StatusError>{s.error}</StatusError> : <StatusOK>доступна</StatusOK>) },
    { title: 'Узлы', render: s => s.nodes.map(n => `${n.name} (${n.maxcpu} vCPU, ${gb(n.maxmem)}, CPU ${Math.round(n.cpu * 100)}%)`).join(', ') || '—' },
    { title: 'Хранилища', render: s => s.storages.map(st => `${st.storage}: ${percent(st.used, st.total)} из ${gb(st.total)}`).join(', ') || '—' },
    { title: 'VM', render: s => `${s.vms.length} (ViVA: ${s.vms.filter(v => v.viva).length})` },
  ];
  return (
    <>
      <Typography variant="body2" paragraph>
        Площадку подключает её администратор: infra/scripts/proxmox-access.sh на узле (пользователь viva@pve, пул viva,
        токены ci и portal), строки PROXMOX_* — в DEPLOY_ENV поставщика. Токен портала — только чтение; VM, пулы и образы
        ViVA создаёт пайплайном viva/clusters токеном ci, права которого — только пул viva.
      </Typography>
      <Table<ProxmoxSite> title="Площадки" options={{ paging: false, search: false }} columns={columns} data={sites} />
    </>
  );
};

const VmsTab = ({ sites }: { sites: ProxmoxSite[] }) => {
  const columns: TableColumn<WithSite<ProxmoxVm>>[] = [
    { title: 'Площадка', field: 'site' },
    { title: 'VMID', field: 'vmid' },
    { title: 'Имя', field: 'name' },
    { title: 'Узел', field: 'node' },
    { title: 'Состояние', render: v => <Status status={v.status} /> },
    { title: 'Адрес', render: v => v.ip ?? '—' },
    { title: 'Ресурсы', render: v => `${v.cores} vCPU, ${v.memoryGb} ГБ, ${v.diskGb} ГБ` },
    { title: 'Пул', render: v => v.pool || '—' },
    { title: 'Чья', render: v => <Owner owner={vmOwner(v)} /> },
    { title: '', render: v => (vmOwner(v) === 'resource' ? <Delete site={v.site} kind="vms" name={v.name} /> : null) },
  ];
  return (
    <>
      <ContentHeader title="Виртуальные машины">
        <Create to={routes.newProxmoxVm} text="Новая VM" />
      </ContentHeader>
      <Typography variant="body2" paragraph>
        VM kubespray-кластеров создаёт форма «Новый Kubernetes-кластер» (Где хосты — VM на площадке Proxmox VE) и
        «Узлы кластера»; удаляет — удаление кластера. Отдельные VM — «Новая VM».
      </Typography>
      <Table<WithSite<ProxmoxVm>> options={{ paging: true, pageSize: 25, search: true }} columns={columns} data={withSite(sites, s => s.vms)} />
    </>
  );
};

const snatText = (n: ProxmoxNetwork) => {
  if (n.kind !== 'vnet') return '—';
  return n.snat ? 'да' : 'нет';
};

const NetworksTab = ({ sites }: { sites: ProxmoxSite[] }) => {
  const columns: TableColumn<WithSite<ProxmoxNetwork>>[] = [
    { title: 'Площадка', field: 'site' },
    { title: 'Сеть', field: 'name' },
    { title: 'Вид', render: n => (n.kind === 'vnet' ? `vnet SDN, зона ${n.zone}` : `bridge узла ${n.node}`) },
    { title: 'Подсеть', render: n => n.cidr ?? '—' },
    { title: 'Шлюз', render: n => n.gateway ?? '—' },
    { title: 'SNAT', render: n => snatText(n) },
  ];
  return (
    <>
      <ContentHeader title="Сети">
        <Create to={routes.newProxmoxNetwork} text="Новая сеть" />
      </ContentHeader>
      <Typography variant="body2" paragraph>
        VM подключаются к существующим vnet SDN и bridge узлов. Новая сеть (vnet с подсетью) применяет SDN площадки —
        только если администратор выдал токену управление SDN (proxmox-access.sh --sdn-manage) в отдельной зоне.
      </Typography>
      <Table<WithSite<ProxmoxNetwork>> options={{ paging: false, search: true }} columns={columns} data={withSite(sites, s => s.networks)} />
    </>
  );
};

const PoolsTab = ({ sites }: { sites: ProxmoxSite[] }) => {
  const columns: TableColumn<WithSite<ProxmoxPool>>[] = [
    { title: 'Площадка', field: 'site' },
    { title: 'Пул', field: 'id' },
    { title: 'Описание', field: 'comment' },
    { title: 'VM', field: 'members' },
    { title: 'Чей', render: p => <Owner owner={poolOwner(p)} /> },
    {
      title: '',
      render: p => (poolOwner(p) === 'resource' && p.members === 0 ? <Delete site={p.site} kind="pools" name={p.id.replace(/^viva\//, '')} /> : null),
    },
  ];
  return (
    <>
      <ContentHeader title="Пулы">
        <Create to={routes.newProxmoxPool} text="Новый пул" />
      </ContentHeader>
      <Typography variant="body2" paragraph>
        Пул viva — все ресурсы ViVA площадки (токен ci пишет только в нём), viva/&lt;кластер&gt; — VM кластера,
        viva/&lt;имя&gt; — пулы раздела для отдельных VM. Удалить можно только пустой пул.
      </Typography>
      <Table<WithSite<ProxmoxPool>> options={{ paging: false, search: true }} columns={columns} data={withSite(sites, s => s.pools)} />
    </>
  );
};

const ImagesTab = ({ sites }: { sites: ProxmoxSite[] }) => {
  const columns: TableColumn<WithSite<ProxmoxImage>>[] = [
    { title: 'Площадка', field: 'site' },
    { title: 'Файл', field: 'name' },
    { title: 'Для образа VM (image)', render: i => (i.content === 'import' ? <code>{i.volid}</code> : 'ISO — не для дисков VM') },
    { title: 'Хранилище', render: i => `${i.storage} (${i.node})` },
    { title: 'Размер', render: i => `${i.sizeGb} ГБ` },
  ];
  return (
    <>
      <ContentHeader title="Образы">
        <Create to={routes.newProxmoxImage} text="Новый образ" />
      </ContentHeader>
      <Typography variant="body2" paragraph>
        Диски VM создаются из файлов content import (через API, без SSH на узел) — их идентификатор указывают в поле
        «Образ» форм кластера и VM. Удалить файл токен ViVA не может: удаление образа раздела только забывает его.
      </Typography>
      <Table<WithSite<ProxmoxImage>> options={{ paging: false, search: true }} columns={columns} data={withSite(sites, s => s.images)} />
    </>
  );
};

export const ProxmoxPage = () => {
  const config = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const enabled = isProxmoxEnabled(config);
  const { loading, error, value } = useAsync(
    async () => (enabled ? fetchProxmoxSites({ discoveryApi, fetchApi }) : []),
    [enabled],
  );

  let body;
  if (!enabled) {
    body = (
      <EmptyState
        missing="info"
        title="Proxmox VE не подключён"
        description="На стенде нет площадок: их строки PROXMOX_CLUSTERS и PROXMOX_<ID>_* добавляют в DEPLOY_ENV поставщика (доступ на площадке — infra/scripts/proxmox-access.sh), затем deploy:stand."
      />
    );
  } else if (loading) {
    body = <Progress />;
  } else if (error) {
    body = <ResponseErrorPanel error={error} />;
  } else {
    const sites = value ?? [];
    body = (
      <TabbedLayout>
        <TabbedLayout.Route path="/sites" title="Площадки">
          <SitesTab sites={sites} />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/vms" title="Виртуальные машины">
          <VmsTab sites={sites} />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/networks" title="Сети">
          <NetworksTab sites={sites} />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/pools" title="Пулы">
          <PoolsTab sites={sites} />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/images" title="Образы">
          <ImagesTab sites={sites} />
        </TabbedLayout.Route>
      </TabbedLayout>
    );
  }
  return (
    <Page themeId="proxmox">
      <Header title="Proxmox VE" subtitle="Площадки установки: VM, сети, пулы и образы" />
      <Content>{body}</Content>
    </Page>
  );
};
